import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type TimeEntry = components['schemas']['TimeEntryResponse'];
export type JobTimeEntries = components['schemas']['JobTimeEntriesResponse'];
export type RunningTimer = components['schemas']['RunningTimerResponse'];
export type LaborItem = components['schemas']['CatalogItemResponse'];

/** The rate tiers, in the order a picker offers them. */
export const RATE_TIERS = ['Standard', 'Overtime', 'Emergency'] as const;
export type RateTier = (typeof RATE_TIERS)[number];

/**
 * Keys. A job's time sits UNDER the job's own key (`['jobs', jobId, 'time-entries']`),
 * so everything that already marks a job stale — creating or voiding its
 * invoice (`['jobs']`), a status change (`['jobs', jobId]`) — marks its time
 * stale too: a draft invoice bills the time, a void releases it.
 */
export const jobTimeKey = (jobId: string) => ['jobs', jobId, 'time-entries'] as const;
export const RUNNING_KEY = ['time-entries', 'running'] as const;
/** Under the price book's key, so saving an item there refreshes the pickers here. */
export const LABOR_ITEMS_KEY = ['catalog-items', 'labor'] as const;

/**
 * A refusal the API explained: a 409's sentence, or a 400's first message and
 * the field it names. Shown verbatim — the screen never paraphrases a rule.
 */
export class TimeEntryRefusedError extends Error {
  constructor(
    message: string,
    public readonly field: string | null = null,
  ) {
    super(message);
    this.name = 'TimeEntryRefusedError';
  }
}

/**
 * ⚠️ Read from openapi-fetch's `error` (M1-05's trap): a 409's string body and
 * a 400's problem land there, never on `data`.
 */
function refusal(status: number, error: unknown, what: string): Error {
  if (status === 409 && typeof error === 'string') {
    return new TimeEntryRefusedError(error);
  }
  if (status === 400) {
    const errors = (error as { errors?: Record<string, string[]> } | undefined)?.errors ?? {};
    const [field, messages] = Object.entries(errors)[0] ?? [null, []];
    if (messages && messages[0]) return new TimeEntryRefusedError(messages[0], field);
  }
  return new Error(`${what} failed: ${status}`);
}

/** A job's time, with the server's clock, or a 403 for a technician not on it. */
export function useJobTimeEntries(jobId: string | undefined) {
  return useQuery({
    queryKey: jobTimeKey(jobId ?? ''),
    enabled: jobId !== undefined,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/jobs/{jobId}/time-entries', {
        params: { path: { jobId: jobId! } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/jobs/{jobId}/time-entries failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

/** The caller's own running timer, anywhere in the account, and the server's clock. */
export function useRunningTimer() {
  return useQuery({
    queryKey: RUNNING_KEY,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/time-entries/running');
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/time-entries/running failed: ${response.status}`);
      }
      return data;
    },
  });
}

/**
 * The account's active labor items, for the timer and the entry form. The
 * price book's list, filtered to Labor — `estimates.build`, so every role
 * reads it (a technician gets no cost, and none is shown here anyway).
 */
export function useLaborItems() {
  return useQuery({
    queryKey: LABOR_ITEMS_KEY,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/catalog-items', {
        params: { query: { type: 'Labor', pageSize: 100 } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/catalog-items?type=Labor failed: ${response.status}`);
      }
      return data.items;
    },
  });
}

/**
 * Every time write marks the job's time and the caller's running timer stale —
 * ⚠️ on a refusal too (`onSettled`, found in review): a 409 says the list on
 * screen is out of date (the time was billed, the job closed, an invoice
 * raised meanwhile), and under the 30s staleTime the row kept its Edit and
 * Delete until something else refetched it.
 */
function useInvalidateTime(jobId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: jobTimeKey(jobId) });
    void queryClient.invalidateQueries({ queryKey: RUNNING_KEY });
  };
}

export function useStartTimer(jobId: string) {
  const invalidate = useInvalidateTime(jobId);
  return useMutation({
    mutationFn: async (body: {
      visitId: string;
      catalogItemId: string;
      rateTier: RateTier;
      notes: string | null;
    }) => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/jobs/{jobId}/time-entries/timer',
        { params: { path: { jobId } }, body },
      );
      if (!response.ok || !data) throw refusal(response.status, error, 'Start timer');
      return data;
    },
    onSettled: invalidate,
  });
}

export function useStopTimer(jobId: string) {
  const invalidate = useInvalidateTime(jobId);
  return useMutation({
    mutationFn: async (entryId: string) => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/time-entries/{entryId}/stop',
        {
          params: { path: { entryId } },
        },
      );
      if (!response.ok || !data) throw refusal(response.status, error, 'Stop timer');
      return data;
    },
    onSettled: invalidate,
  });
}

export interface TimeEntryBody {
  userId?: string | null;
  visitId: string | null;
  catalogItemId: string;
  rateTier: RateTier;
  startedAt: string;
  endedAt: string | null;
  minutes: number | null;
  notes: string | null;
}

/** Adds time by hand (`entryId` undefined) or corrects an entry. */
export function useSaveTimeEntry(jobId: string, entryId?: string) {
  const invalidate = useInvalidateTime(jobId);
  return useMutation({
    mutationFn: async ({ userId, ...body }: TimeEntryBody) => {
      // Who did the work is set once, when the time is added; an edit never moves it.
      const result =
        entryId === undefined
          ? await apiClient.POST('/api/v1/jobs/{jobId}/time-entries', {
              params: { path: { jobId } },
              body: { ...body, userId: userId ?? null },
            })
          : await apiClient.PUT('/api/v1/time-entries/{entryId}', {
              params: { path: { entryId } },
              body,
            });
      if (!result.response.ok || !result.data) {
        throw refusal(result.response.status, result.error, 'Save time');
      }
      return result.data;
    },
    onSettled: invalidate,
  });
}

export function useDeleteTimeEntry(jobId: string) {
  const invalidate = useInvalidateTime(jobId);
  return useMutation({
    mutationFn: async (entryId: string) => {
      const { error, response } = await apiClient.DELETE('/api/v1/time-entries/{entryId}', {
        params: { path: { entryId } },
      });
      if (!response.ok) throw refusal(response.status, error, 'Delete time');
    },
    onSettled: invalidate,
  });
}
